import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import type { Props as ExternalInfiniteScrollProps } from 'react-infinite-scroll-component';
import { default as ExternalInfiniteScroll } from 'react-infinite-scroll-component';
import type { InfiniteScrollStore } from '../../lib';
import { intl, useTrackElementResize } from '../../lib';
import { LoadMask } from './LoadMask';
import { isEmptyString } from '../../util';

/**
 * Props for an {@link InfiniteScroll} component.
 */
export interface InfiniteScrollProps<Data, Params>
  extends Omit<
    ExternalInfiniteScrollProps,
    'scrollableTarget' | 'hasMore' | 'loader' | 'dataLength' | 'endMessage'
  > {
  /**
   * Id of the scroll target created by the {@link InfiniteScroll} component.
   * This id is absolutely necessary for the component to work!
   */
  scrollTargetId: string;
  /**
   * The store that the component depends on.
   * Loading of the next page is dependent on the store's <code>loadedCount</code> and <code>totalCount</code> properties.
   * TODO: refactor to use a store hook and just select what's needed from it in the implementation. To reduce the number of renders.
   */
  store: InfiniteScrollStore<Data, Params>;
  /**
   * Message to be displayed when the bottom has been reached.
   */
  endMessage?: string;
  /**
   * Number of rows in single page, 10 is used as default value if not provided
   */
  pageSize?: number;
}

/**
 * Custom InfiniteScroll component that makes use of the 'react-infinite-scroll-component' InfiniteScroll.
 *
 * This component is primarily used to fix an issue with the 'react-infinite-scroll-component' InfiniteScroll
 * where scrolling only works if the scroll-target always has a scrollbar. Because of this, this component uses a
 * layoutEffect to automatically load the next page if no scrollbar is visible until either the scrollTarget has a
 * scrollbar or all the records have been loaded in the store.
 * For better use with screen readers it has role feed and its children should be of role article.
 *
 * props an {@link InfiniteScrollProps} object.
 * @return the created {@link JSX.Element}
 * @constructor
 */
export function InfiniteScroll<Data, Params>({
  scrollTargetId,
  store,
  next,
  children,
  endMessage,
  pageSize = 10,
  style,
  height,
  inverse,
  className,
  onScroll,
  ...infiniteScrollProps
}: Readonly<InfiniteScrollProps<Data, Params>>): JSX.Element {
  // the InfiniteScroll only works properly when the first page makes the target scroll element actually scrollable
  // this layoutEffect is a workaround to make it automatically load pages until there are either no more pages left, or the element becomes scrollable
  const infiniteScrollWatchDiv = useRef<HTMLDivElement>(null);
  const infiniteScrollTarget = useRef<HTMLDivElement>(null);

  const getLastLoadedPageSize = useCallback(state => {
    if (state.pages.length === 0) {
      return 0;
    }

    return state.pages[state.pages.length - 1]?.length;
  }, []);

  const hasMorePagesToLoad = useMemo(() => {
    return (
      (store.totalCount >= 0 && store.loadedCount < store.totalCount) ||
      (store.totalCount < 0 && getLastLoadedPageSize(store) >= pageSize)
    );
  }, [getLastLoadedPageSize, pageSize, store]);

  const autoCallNextCb = useCallback(() => {
    if (
      !store.loading &&
      store.wasLoaded &&
      hasMorePagesToLoad &&
      infiniteScrollWatchDiv.current &&
      infiniteScrollTarget.current &&
      infiniteScrollWatchDiv.current.clientHeight <
        infiniteScrollTarget.current.clientHeight
    ) {
      next();
    }
  }, [store.loading, store.wasLoaded, hasMorePagesToLoad, next]);

  useLayoutEffect(autoCallNextCb, [autoCallNextCb]);
  // this resize tracker is needed to make the InfiniteScroll not break when the window is resized
  // e.g.: Switching from portrait to landscape display
  useTrackElementResize(infiniteScrollTarget, true, autoCallNextCb);

  // the endMessage text
  const endMessageText = isEmptyString(endMessage)
    ? intl('common.endofData')
    : endMessage;

  // render the list
  return (
    <div
      id={scrollTargetId}
      ref={infiniteScrollTarget}
      className="ehs-infinite-scroll"
      {...infiniteScrollProps}
    >
      <div ref={infiniteScrollWatchDiv} role="feed">
        <ExternalInfiniteScroll
          scrollableTarget={scrollTargetId}
          next={next}
          hasMore={hasMorePagesToLoad}
          dataLength={store.loadedCount}
          // the loader is only shown when the ExternalInfiniteScroll called next
          // since we depend on a store, obviously we want the loader to be shown when the store is loading
          // therefore this is undefined and a custom loader is added below the children instead
          loader={undefined}
          endMessage={
            <>
              {store.wasLoaded && (
                <p style={{ textAlign: 'center' }}>
                  <b>{endMessageText}</b>
                </p>
              )}
            </>
          }
          style={style}
          height={height}
          inverse={inverse}
          className={className}
          onScroll={onScroll}
          {...infiniteScrollProps}
        >
          {children}
          {store.loading && (
            <div className="custom-loader">
              <LoadMask masked={store.loading} />
            </div>
          )}
        </ExternalInfiniteScroll>
      </div>
    </div>
  );
}
